import React, { useState, useRef, useEffect } from "react";
import { GoogleMap, LoadScript, Autocomplete, DirectionsService, DirectionsRenderer } from "@react-google-maps/api";
import { GoogleKeys } from "../../config/google";

const libraries = ["places"];
const mapContainerStyle = { width: "100%", height: "400px" };
const center = { lat: 28.6139, lng: 77.209 }; // Default: Delhi

const AddPropertyComponent = () => {
  const [map, setMap] = useState(null);
  const [landmarks, setLandmarks] = useState([]);
  const autocompleteRef = useRef(null);
  const apiKey = GoogleKeys.GOOGLE_MAPS_API_KEY;

  const [userLocation, setUserLocation] = useState(null);
  const [destination, setDestination] = useState(null);
  const [directions, setDirections] = useState(null);

  useEffect(() => {
    if (navigator.geolocation) {
      navigator.geolocation.getCurrentPosition((position) => {
        setUserLocation({
          lat: position.coords.latitude,
          lng: position.coords.longitude
        });
      });
    }
  }, []);

  const onPlaceSelect = () => {
    const place = autocompleteRef.current.getPlace();
    if (!place.geometry) return;

    const location = {
      lat: place.geometry.location.lat(),
      lng: place.geometry.location.lng(),
    };

    setDestination(location);
    fetchNearbyLandmarks(location.lat, location.lng);
  };

  // 🔹 **Calculate Distance (Haversine Formula)**
  const calculateDistance = (lat1, lon1, lat2, lon2) => {
    const R = 6371; // Radius of Earth in km
    const dLat = (lat2 - lat1) * (Math.PI / 180);
    const dLon = (lon2 - lon1) * (Math.PI / 180);
    const a =
      Math.sin(dLat / 2) * Math.sin(dLat / 2) +
      Math.cos(lat1 * (Math.PI / 180)) *
        Math.cos(lat2 * (Math.PI / 180)) *
        Math.sin(dLon / 2) *
        Math.sin(dLon / 2);
    const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
    return R * c; // Distance in km
  };

  // 🔹 **Fetch Nearby Landmarks & Sort by Distance**
  const fetchNearbyLandmarks = async (lat, lng) => {
    if (!map) return;

    const service = new window.google.maps.places.PlacesService(map);
    const request = {
      location: new window.google.maps.LatLng(lat, lng),
      radius: 2000,
      type: "point_of_interest",
    };

    service.nearbySearch(request, (results, status) => {
      if (status === window.google.maps.places.PlacesServiceStatus.OK) {
        // 🔹 **Sort landmarks by closest first**
        const sortedLandmarks = results
          .map((landmark) => ({
            ...landmark,
            distance: calculateDistance(lat, lng, landmark.geometry.location.lat(), landmark.geometry.location.lng()),
          }))
          .sort((a, b) => a.distance - b.distance); // Sort by distance (ascending)

        setLandmarks(sortedLandmarks.slice(0, 10)); // Get top 10 closest landmarks
      }
    });
  };

  const fetchDirections = () => {
    if (!userLocation || !destination || !map) return;

    const directionsService = new window.google.maps.DirectionsService();
    directionsService.route(
      {
        origin: userLocation,
        destination: destination,
        travelMode: window.google.maps.TravelMode.DRIVING,
      },
      (result, status) => {
        if (status === window.google.maps.DirectionsStatus.OK) {
          setDirections(result);
        } else {
          console.error("Directions request failed due to " + status);
        }
      }
    );
  };

  const openGoogleMaps = () => {
    if (!userLocation || !destination) return;

    const url = `https://www.google.com/maps/dir/?api=1&origin=${userLocation.lat},${userLocation.lng}&destination=${destination.lat},${destination.lng}&travelmode=driving`;
    window.open(url, "_blank");
  };

  return (
    <div className="py-[200px]">
      <LoadScript googleMapsApiKey={apiKey} libraries={libraries}>
        <Autocomplete onLoad={(autocomplete) => (autocompleteRef.current = autocomplete)} onPlaceChanged={onPlaceSelect}>
          <input type="text" placeholder="Enter a location" className="border p-2 w-full" />
        </Autocomplete>

        <GoogleMap mapContainerStyle={mapContainerStyle} center={center} zoom={12} onLoad={(map) => setMap(map)}>
          {directions && <DirectionsRenderer directions={directions} />}
        </GoogleMap>

        <div className="mt-4">
          <h2 className="text-lg font-bold">Nearby Landmarks (Sorted by Distance)</h2>
          <ul>
            {landmarks.map((landmark, index) => (
              <li key={index}>
                {landmark.name} - {landmark.distance.toFixed(2)} km
              </li>
            ))}
          </ul>
        </div>

        {userLocation && destination && (
          <div className="mt-4 flex gap-4">
            <button className="bg-blue-500 text-white px-4 py-2 rounded" onClick={fetchDirections}>
              Show Directions
            </button>
            <button className="bg-green-500 text-white px-4 py-2 rounded" onClick={openGoogleMaps}>
              View Directions
            </button>
          </div>
        )}
      </LoadScript>
    </div>
  );
};

export default AddPropertyComponent;
